import { Typography, FormGroup, InputLabel } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import CurrencyTextField from '@unicef/material-ui-currency-textfield'
import useStyles from './priceProduct.styles'
import { useProduct } from 'contextApi/ProductContext'
import { LUEGOPAGO_VERTICAL_ID } from 'constants/appConstants'

const PriceRangeProduct = ({ setDisableNextButton }) => {
  const { product, setProduct } = useProduct()
  const initialRange =
    product?.rangePrice && typeof product.rangePrice === 'object'
      ? {
          minPrice: product.rangePrice.minPrice ?? 10000,
          maxPrice: product.rangePrice.maxPrice ?? 990000,
        }
      : { minPrice: 10000, maxPrice: 990000 }
  const [rangePrice, setRangePrice] = useState(initialRange)
  const classes = useStyles()

  const updateVerticalInfo = (verticalInfo, regularPrice) => {
    if (!Array.isArray(verticalInfo)) {
      return verticalInfo
    }

    return verticalInfo.map((vertical) => {
      if (vertical.vertical !== LUEGOPAGO_VERTICAL_ID) {
        return vertical
      }

      return {
        ...vertical,
        regularPrice,
      }
    })
  }

  const handleChange = (name) => (event, value) => {
    let numericValue = Number(value)
    const newRange = { ...rangePrice, [name]: numericValue }
    setRangePrice(newRange)

    const minValid = newRange.minPrice >= 10000 && newRange.minPrice <= 990000
    const maxValid =
      newRange.maxPrice >= 10000 &&
      newRange.maxPrice <= 990000 &&
      newRange.maxPrice > newRange.minPrice

    // setProduct solo si ambos son válidos
    if (minValid && maxValid) {
      setProduct((prev) => ({
        ...prev,
        rangePrice: { minPrice: newRange.minPrice, maxPrice: newRange.maxPrice },
        regularPrice: newRange.minPrice,
        verticalInfo: updateVerticalInfo(prev.verticalInfo, newRange.minPrice),
      }))
      setDisableNextButton(false)
    } else {
      setDisableNextButton(true)
    }
  }

  useEffect(() => {
    setProduct((prev) => ({
      ...prev,
      rangePrice: { minPrice: rangePrice.minPrice, maxPrice: rangePrice.maxPrice },
      regularPrice: rangePrice.minPrice,
      verticalInfo: updateVerticalInfo(prev.verticalInfo, rangePrice.minPrice),
    }))
  }, [])

  const minPriceError = rangePrice.minPrice < 10000 || rangePrice.minPrice > 990000
  const maxPriceError =
    rangePrice.maxPrice < (rangePrice.minPrice || 10000) || rangePrice.maxPrice > 990000
  const equalPriceError = rangePrice.maxPrice === rangePrice.minPrice

  let helperText = ''
  let showError = false
  if (rangePrice.minPrice < 10000) {
    helperText = 'El precio mínimo debe ser igual o mayor a $10,000.'
    showError = true
  } else if (rangePrice.minPrice > 990000) {
    helperText = 'El precio mínimo no puede ser mayor a $990,000.'
    showError = true
  } else if (rangePrice.maxPrice > 990000) {
    helperText = 'El precio máximo no puede ser mayor a $990,000.'
    showError = true
  } else if (rangePrice.maxPrice < rangePrice.minPrice) {
    helperText = 'El precio máximo debe ser igual o mayor al precio mínimo.'
    showError = true
  } else if (rangePrice.maxPrice < 10000) {
    helperText = 'El precio máximo debe ser igual o mayor a $10,000.'
    showError = true
  } else if (equalPriceError) {
    helperText = 'El precio mínimo y máximo no pueden ser iguales.'
    showError = true
  } else {
    helperText = 'El precio mínimo permitido es $10,000 y el máximo $990,000.'
  }

  return (
    <div>
      <Typography variant="subtitle1">Precio de Producto Recargable</Typography>
      <Typography variant="body2" color="inherit">
        Establece los montos mínimo y máximo de recarga para tu tarjeta de regalo o producto
        prepago.
      </Typography>
      <div className={classes.container + ' ' + classes.formSpacing}>
        <FormGroup className={classes.formGroup}>
          <InputLabel error={minPriceError}>Precio Mínimo ($)</InputLabel>
          <CurrencyTextField
            currencySymbol="$"
            textAlign="left"
            decimalPlaces={0}
            outputFormat="string"
            onChange={handleChange('minPrice')}
            value={rangePrice.minPrice}
            variant="outlined"
            name="minPrice"
            id="minPrice"
            error={minPriceError}
          />
        </FormGroup>
        <FormGroup className={classes.formGroup}>
          <InputLabel error={maxPriceError}>Precio Máximo ($)</InputLabel>
          <CurrencyTextField
            currencySymbol="$"
            textAlign="left"
            decimalPlaces={0}
            outputFormat="string"
            onChange={handleChange('maxPrice')}
            value={rangePrice.maxPrice}
            variant="outlined"
            name="maxPrice"
            id="maxPrice"
            error={maxPriceError}
          />
        </FormGroup>
      </div>
      <Typography
        variant="caption"
        style={{ color: showError ? '#f44336' : undefined, marginTop: 8, display: 'block' }}
      >
        {helperText}
      </Typography>
    </div>
  )
}

PriceRangeProduct.propTypes = {
  setDisableNextButton: PropTypes.func.isRequired,
}

export default PriceRangeProduct
